<template>
  <WdsStack class="workflow-card" direction-column justify-space-between data-test="workflow-card">
    <div class="tw-m-6" :data-test="`workflow-card-${workflow.id}`">
      <div class="workflow-header">
        <WdsStack justify-space-between align-center>
          <WdsStack gap-md align-center>
            <div class="icon">
              <WdsIcon v-if="trigger" :name="trigger.icon" color="#5D36F1" />
            </div>
            <WdsStack direction-column justify-center>
              <WdsText large>{{ workflow.name }}</WdsText>
              <WdsText v-if="trigger" secondary>{{ trigger.label }}</WdsText>
            </WdsStack>
          </WdsStack>

          <WdsMenu global placement="bottom-end" :offset-from-trigger-element="10">
            <template #trigger>
              <WdsButtonIcon
                :data-test="`open-workflow-card-options-${workflow.id}`"
                transparent-background
                aria-haspopup="true"
              >
                <WdsIcon name="overflow-menu-horizontal" />
              </WdsButtonIcon>
            </template>
            <WdsMenuOption :data-test="`open-edit-workflow-${workflow.id}`" @click="$emit('edit')">
              <WdsIcon name="edit" />
              {{ $t('Edit') }}
            </WdsMenuOption>
            <WdsMenuOption :disabled="duplicatingWorkflow" @click="$emit('duplicate', workflow)">
              <WdsIcon v-if="!duplicatingWorkflow" name="copy" weight="thick" />
              <WdsSpinner v-else indeterminate />
              <span class="tw-whitespace-nowrap">{{ $t('Duplicate') }}</span>
            </WdsMenuOption>
            <WdsMenuOption
              theme="DESTRUCTIVE"
              :data-test="`open-delete-workflow-modal-${workflow.id}`"
              @click="$emit('delete', { id: workflow.id, name: workflow.name })"
            >
              <WdsIcon name="trash-can" />
              {{ $t('Delete') }}
            </WdsMenuOption>
          </WdsMenu>
        </WdsStack>
      </div>
      <div class="workflow-info">
        <WdsStack justify-space-between>
          <WdsStack direction-column>
            <WdsText color="#D9D9D9" secondary>
              {{ date }}
              <div class="dot"></div>
              <WdsTag sm :success="status.success">{{ status.title }}</WdsTag>
            </WdsText>
          </WdsStack>
        </WdsStack>
        <span class="description">
          {{ workflow.description }}
        </span>
      </div>
    </div>
    <div v-if="false" class="workflow-footer"></div>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsStack,
  WdsTag,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsButtonIcon,
  WdsSpinner,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { Workflow } from '@/components/Workflows/Pages/Workflow/workflow'
import { ApprovalStatus } from '@/gql/graphql'
import { triggerOptions } from '@/components/Workflows/utils'

defineEmits<{
  edit: []
  duplicate: [workflow: Workflow]
  delete: [{ id: string; name: string }]
}>()

const props = withDefaults(
  defineProps<{
    workflow: Workflow
    duplicatingWorkflow: boolean
  }>(),
  { duplicatingWorkflow: false },
)

const status = computed(() => {
  switch (props.workflow.status) {
    case ApprovalStatus.Active:
      return { title: $t('Live'), success: true }
    case ApprovalStatus.Inactive:
      return { title: $t('Not live'), success: false }
    case ApprovalStatus.Archived:
      return { title: $t('Archived'), success: false }
  }
  return { title: 'Failed', success: false }
})

const trigger = triggerOptions.find(({ value }) => value === props.workflow.trigger)

const date = computed(() => {
  const d = new Date(props.workflow.createdAt)
  return new Intl.DateTimeFormat(currentLanguageSelected, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(d)
})
</script>

<style lang="scss" scoped>
@tailwind base;

.workflow-card {
  @apply tw-border;
  @apply tw-rounded;
  @apply tw-border-solid;
  @apply tw-border-gray-200;
  @apply tw-max-w-[442px];

  .workflow-header {
    .icon {
      @apply tw-p-2;
      @apply tw-bg-indigo-50;
      @apply tw-rounded;
    }
  }

  .workflow-footer {
    padding: 18px 24px;
    border-top: 1px solid;
    @apply tw-border-gray-200;
  }

  .workflow-info {
    @apply tw-mt-4;
    @apply tw-font-normal;
    @apply tw-max-w-[400px];

    .description {
      @apply tw-mt-4;
      @apply tw-text-gray-700;
      line-height: 20px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }

    .dot {
      --dot-color: #d9d9d9;
      display: inline-block;
      background-color: var(--dot-color);
      border: solid var(--dot-color) 1px;
      font-weight: 400;
      margin: 0 8px 2px;
      border-radius: 50%;
      height: 4px;
      width: 4px;
    }
    &::v-deep(.wds-text-secondary) {
      @apply tw-text-gray-600;
    }
  }
}
</style>
